import {
  EnumOrderDeliveryMethod,
  OrderByIdQuery,
  OrderPricesByIdQuery,
  RenewablePurchaseByIdQuery,
  RenewablePurchaseByIdPublicQuery,
} from "gql/generated";
import { CreditCard } from "iconoir-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "styled-system/jsx";
import { Product } from "gql/generated";
import { CheckoutSummary } from "widgets/booker/shared/CheckoutSummary";
import { ReservationSection } from "./ReservationSection";
import { isEftPendingChargeId } from "shared/utils";
import { isLegacyImportedOrder } from "shared/utils/legacySilkstartPdf";
import {
  buildOrderAdditionalLineItems,
  buildOrderCartItems,
  buildOrderReceiptDiscounts,
} from "./resolveOrderReceiptLines";

type RenewablePurchase =
  | RenewablePurchaseByIdQuery["renewablePurchaseById"]
  | RenewablePurchaseByIdPublicQuery["renewablePurchaseByIdPublic"]
  | null
  | undefined;

type ReceiptProps = {
  order: OrderByIdQuery["orderById"];
  orderPrices: OrderPricesByIdQuery["orderPricesById"];
  orderPaymentMetadata?: NonNullable<
    OrderByIdQuery["orderById"]
  >["paymentMetadata"];
  renewablePurchase?: RenewablePurchase;
};

export const Receipt = ({
  order,
  orderPrices,
  orderPaymentMetadata,
  renewablePurchase,
}: ReceiptProps) => {
  const { t } = useTranslation("storefront", {
    keyPrefix: "RESERVATION_DETAILS.RECEIPT",
  });

  const cart = useMemo(() => {
    if (!order || !orderPrices) return [];
    return buildOrderCartItems(order, orderPrices, renewablePurchase);
  }, [order, orderPrices, renewablePurchase]);

  const discounts = useMemo(() => {
    if (!order || !orderPrices) return [];
    return buildOrderReceiptDiscounts(
      order,
      orderPrices,
      renewablePurchase,
      t("OFF"),
      t("OFF_PER_TICKET"),
      t("FLAT_RATE"),
    );
  }, [order, orderPrices, renewablePurchase, t]);

  const additionalLineItems = useMemo(() => {
    if (!order) return [];
    return buildOrderAdditionalLineItems(order, cart, renewablePurchase);
  }, [order, cart, renewablePurchase]);

  if (!order || !orderPrices) return null;

  return (
    <ReservationSection icon={CreditCard} title={t("TITLE")}>
      <Box
        bgColor="ink.100"
        border="2px solid"
        borderColor="border.light"
        borderRadius="100"
        p="300"
      >
        <CheckoutSummary
          isReservationPage={true}
          cart={cart}
          subTotal={orderPrices.subTotal}
          grandTotal={orderPrices.grandTotal}
          tax={orderPrices.taxTotal}
          taxLines={orderPrices.taxLines}
          customerFee={orderPrices.customerFee}
          discounts={discounts}
          depositSettings={orderPaymentMetadata?.depositSettings}
          depositTotal={orderPrices.depositTotal}
          shippingTotal={
            order.delivery?.method === EnumOrderDeliveryMethod.Ship
              ? (orderPrices.shippingTotal ?? 0)
              : null
          }
          orderId={order._id}
          isManualOrder={!!order.stripeMetadata.invoiceId}
          isEftPending={isEftPendingChargeId(order.stripeMetadata.chargeId)}
          manualOrderInvoiceStatus={
            order.stripeMetadata.invoiceStatus ?? undefined
          }
          mainProduct={order.purchasedProducts[0]?.product as Product}
          additionalLineItems={additionalLineItems}
          taxExemptionClaimed={!!order.taxExemption?.claimed}
          isLegacyOrder={isLegacyImportedOrder(order._id)}
          complimentaryAmount={order.complimentaryAmount}
        />
      </Box>
    </ReservationSection>
  );
};
